import React, { useState, useEffect } from "react";
import { useNavigationEvent } from "navigation-react";
import Header from "./Header";
import Footer from "./Footer";
import slugify from "./slugify";

const API_URL = "https://proseppos.salcommsystems.click/product-list-api";
const API_BASE = "https://proseppos.salcommsystems.click";
const FALLBACK_LOGO =
  "https://prosepservices.com/_next/static/media/logo.f3c3e836.png";

function ProductDetails() {
  const { stateNavigator, data } = useNavigationEvent();

  const productslug = data?.productslug;

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);

  // ============ FETCH PRODUCT BY SLUG ============
  useEffect(() => {
    if (!productslug) {
      setError("No product slug in URL");
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch(API_URL)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load products");
        return res.json();
      })
      .then((list) => {
        const found = list.find((p) => slugify(p.title) === productslug);
        if (!found) {
          setError(`No product found for: ${productslug}`);
        } else {
          setProduct(found);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [productslug]);

  // ============ SET PAGE TITLE + META ============
  useEffect(() => {
    if (product) {
      document.title = `${product.title} | Prosep Services`;

      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content =
        product.field_description ||
        `${product.title} — available at Prosep Services.`;

      return () => {
        document.title = "Prosep Services";
      };
    }
  }, [product]);

  // ============ LOADING ============
  if (loading) {
    return (
      <>
        <Header />
        <section className="pd-wrap">
          <p className="pd-status">Loading product...</p>
        </section>
        <Footer />
        <style>{baseStyles}</style>
      </>
    );
  }

  // ============ ERROR ============
  if (error || !product) {
    return (
      <>
        <Header />
        <section className="pd-wrap">
          <p className="pd-status">
            {error || "Product not found."}
          </p>
          <p className="pd-status" style={{ fontSize: 12 }}>
            URL slug: {productslug || "(none)"}
          </p>
        </section>
        <Footer />
        <style>{baseStyles}</style>
      </>
    );
  }

  // ============ PRODUCT ============
  const getImageUrl = (p) =>
    p.field_product_image
      ? `${API_BASE}${p.field_product_image}`
      : FALLBACK_LOGO;

  const isFallback = !product.field_product_image;
  const inStock = Number(product.field_stock_quantity) > 0;

  // Strip HTML tags from rich-text field
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const descriptionText = stripHtml(product.field_description);

  const handleAddToCart = () => {
    alert(`Added ${quantity} × ${product.title} to cart`);
  };

  return (
    <>
      <Header />

      <section className="pd-wrap">
        <div className="pd-layout">
          <div className="pd-image-box">
            <img
              src={getImageUrl(product)}
              alt={product.title}
              className={isFallback ? "pd-logo-fallback" : ""}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = FALLBACK_LOGO;
                e.target.classList.add("pd-logo-fallback");
              }}
            />
          </div>

          <div className="pd-info">
            <p className="pd-category">
              {product.field_category || "Uncategorized"}
            </p>
            <h1 className="pd-title">{product.title}</h1>
            <p className="pd-price">
              GH₵ {Number(product.field_price).toFixed(2)}
            </p>

            {descriptionText && (
              <div className="pd-description">
                <h3 className="pd-description-title">Description</h3>
                <p className="pd-description-text">{descriptionText}</p>
              </div>
            )}

            <div className="pd-meta">
              <p><strong>SKU:</strong> {product.field_sku}</p>
              {product.field_gtin && (
                <p><strong>GTIN:</strong> {product.field_gtin}</p>
              )}
              <p className={inStock ? "pd-in" : "pd-out"}>
                {inStock
                  ? `In Stock (${product.field_stock_quantity} available)`
                  : "Out of Stock"}
              </p>
            </div>

            <div className="pd-buy">
              <div className="pd-qty">
                <button
                  className="pd-qty-btn"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={!inStock || quantity <= 1}
                >
                  −
                </button>
                <span className="pd-qty-value">{quantity}</span>
                <button
                  className="pd-qty-btn"
                  onClick={() => setQuantity((q) => q + 1)}
                  disabled={!inStock}
                >
                  +
                </button>
              </div>

              <button
                className="pd-btn-primary"
                onClick={handleAddToCart}
                disabled={!inStock}
              >
                {inStock ? "Add to Cart" : "Out of Stock"}
              </button>

              <button className="pd-btn-secondary">Enquire</button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{baseStyles}</style>
    </>
  );
}

const baseStyles = `
  .pd-wrap {
    max-width: 1400px;
    margin: 60px auto;
    padding: 0 40px;
    font-family: Arial, sans-serif;
    box-sizing: border-box;
    min-height: 60vh;
  }
  .pd-back {
    background: #111;
    color: #fff;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 28px;
    font-family: inherit;
    transition: background 0.15s ease;
  }
  .pd-back:hover { background: #c8102e; }

  .pd-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    align-items: start;
  }

  .pd-image-box {
    width: 100%;
    height: 520px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
    overflow: hidden;
    position: relative;
    cursor: zoom-in;
  }
  .pd-image-box img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: center center;
  }
  .pd-image-box:hover img {
    transform: scale(1.6);
  }
  .pd-image-box:hover img.pd-logo-fallback {
    transform: none;
  }
  .pd-image-box img.pd-logo-fallback {
    max-width: 60%;
    max-height: 50%;
    opacity: 0.85;
  }

  .pd-info { display: flex; flex-direction: column; }
  .pd-category {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #c8102e;
    margin: 0 0 12px;
  }
  .pd-title {
    font-size: 34px;
    font-weight: 900;
    color: #111;
    line-height: 1.2;
    margin: 0 0 20px;
  }
  .pd-price {
    font-size: 28px;
    font-weight: 800;
    color: #c8102e;
    margin: 0 0 24px;
  }

  .pd-description {
    margin-bottom: 24px;
  }
  .pd-description-title {
    font-size: 16px;
    font-weight: 800;
    color: #111;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e5e7eb;
  }
  .pd-description-text {
    font-size: 15px;
    color: #333;
    line-height: 1.7;
    margin: 0;
    white-space: pre-line;
  }

  .pd-meta {
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 20px 0;
    margin-bottom: 28px;
  }
  .pd-meta p {
    margin: 8px 0;
    font-size: 15px;
    color: #333;
  }
  .pd-meta strong { color: #111; margin-right: 6px; }
  .pd-in { color: #15803d; font-weight: 700; }
  .pd-out { color: #b91c1c; font-weight: 700; }

  .pd-buy {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    align-items: center;
  }

  .pd-qty {
    display: flex;
    align-items: center;
    border: 2px solid #111;
    border-radius: 6px;
    overflow: hidden;
    height: 52px;
  }
  .pd-qty-btn {
    background: #fff;
    border: none;
    width: 44px;
    height: 100%;
    font-size: 22px;
    font-weight: 700;
    color: #111;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s ease;
  }
  .pd-qty-btn:hover:not(:disabled) {
    background: #f3f4f6;
    color: #c8102e;
  }
  .pd-qty-btn:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }
  .pd-qty-value {
    min-width: 48px;
    text-align: center;
    font-size: 17px;
    font-weight: 700;
    color: #111;
    border-left: 2px solid #111;
    border-right: 2px solid #111;
    line-height: 48px;
  }

  .pd-btn-primary,
  .pd-btn-secondary {
    padding: 16px 32px;
    border-radius: 6px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    letter-spacing: 0.5px;
    font-family: inherit;
    transition: all 0.15s ease;
    border: none;
    height: 52px;
  }
  .pd-btn-primary {
    background: #111;
    color: #fff;
    flex: 1 1 200px;
  }
  .pd-btn-primary:hover:not(:disabled) { background: #c8102e; }
  .pd-btn-primary:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
  .pd-btn-secondary {
    background: #fff;
    color: #111;
    border: 2px solid #111;
  }
  .pd-btn-secondary:hover {
    background: #111;
    color: #fff;
  }

  .pd-status {
    text-align: center;
    color: #6b7280;
    padding: 40px 0;
  }

  @media (max-width: 900px) {
    .pd-layout {
      grid-template-columns: 1fr;
      gap: 28px;
    }
    .pd-image-box { height: 380px; }
    .pd-title { font-size: 26px; }
    .pd-price { font-size: 22px; }
  }

  @media (max-width: 500px) {
    .pd-wrap { padding: 0 20px; margin: 30px auto; }
    .pd-image-box { height: 280px; padding: 16px; }
    .pd-title { font-size: 22px; }
    .pd-buy { flex-direction: column; align-items: stretch; }
    .pd-qty { justify-content: space-between; }
    .pd-btn-primary,
    .pd-btn-secondary { width: 100%; }
  }
`;

export default ProductDetails;